'use client';

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Typography, Row, Col, Space, Button,
  Input, Select, Modal, Form, Popconfirm, InputNumber,
} from 'antd';
import { toast } from 'react-toastify';
import {
  PlusOutlined, SearchOutlined, ReloadOutlined,
  EditOutlined, DeleteOutlined, EyeOutlined, HomeOutlined,
} from '@ant-design/icons';
import { useRouter } from 'next/navigation';
import { WarehouseRecord } from '@/lib/mockWarehouseStore';
import { useAuth } from '@/components/AuthContext';

const { Title, Text } = Typography;

const WAREHOUSE_TYPES = [
  { value: 'MAIN', label: 'Gudang Utama (Main)' },
  { value: 'SUPPORT', label: 'Gudang Penunjang (Support)' },
  { value: 'QUARANTINE', label: 'Gudang Karantina / Khusus' },
  { value: 'FINISHED_GOODS', label: 'Gudang Barang Jadi' },
  { value: 'RAW_MATERIALS', label: 'Gudang Bahan Baku' },
];

const TYPE_COLORS: Record<string, string> = {
  MAIN: 'blue', SUPPORT: 'cyan', QUARANTINE: 'red',
  FINISHED_GOODS: 'green', RAW_MATERIALS: 'orange',
};
const TYPE_LABELS: Record<string, string> = {
  MAIN: 'Gudang Utama', SUPPORT: 'Gudang Penunjang', QUARANTINE: 'Gudang Karantina',
  FINISHED_GOODS: 'Barang Jadi', RAW_MATERIALS: 'Bahan Baku',
};

export default function WarehousesPage() {
  const router = useRouter();
  const { user } = useAuth();
  const isManager = user.roles.includes('ADMIN') || user.roles.includes('WAREHOUSE');

  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingWh, setEditingWh] = useState<WarehouseRecord | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchWarehouses = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (typeFilter !== 'ALL') params.set('type', typeFilter);
      const res = await fetch(`/api/warehouses?${params}`);
      const json = await res.json();
      if (json.success) setWarehouses(json.data);
    } catch {
      toast.error('Gagal memuat data gudang');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, typeFilter]);

  useEffect(() => { fetchWarehouses(); }, [fetchWarehouses]);

  const handleOpenCreate = () => {
    setEditingWh(null);
    form.resetFields();
    form.setFieldsValue({ status: 'ACTIVE', capacityUnit: 'pallet' });
    setModalOpen(true);
  };

  const handleOpenEdit = (record: WarehouseRecord) => {
    setEditingWh(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSubmitForm = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      let res: Response;
      if (editingWh) {
        res = await fetch(`/api/warehouses/${editingWh.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
      } else {
        res = await fetch('/api/warehouses', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
      }

      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error);
      toast.success(json.message);
      setModalOpen(false);
      fetchWarehouses();
    } catch (err: unknown) {
      if (err instanceof Error) toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await fetch(`/api/warehouses/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error);
      toast.success(json.message);
      fetchWarehouses();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Gagal menghapus data gudang');
    }
  };

  const activeCount = warehouses.filter((w) => w.status === 'ACTIVE').length;

  const columns = [
    {
      title: 'Kode Gudang',
      dataIndex: 'code',
      key: 'code',
      width: 140,
      render: (c: string, r: WarehouseRecord) => (
        <span
          style={{ fontWeight: 800, color: '#6366F1', cursor: 'pointer', fontSize: 13 }}
          onClick={() => router.push(`/master/warehouses/${r.id}`)}
        >
          {c}
        </span>
      ),
    },
    {
      title: 'Nama Gudang',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, r: WarehouseRecord) => (
        <div>
          <span
            style={{ fontWeight: 700, color: '#0F172A', cursor: 'pointer', fontSize: 13 }}
            onClick={() => router.push(`/master/warehouses/${r.id}`)}
          >
            {name}
          </span>
          {r.address && (
            <div style={{ color: '#64748B', fontSize: 11, marginTop: 2 }}>{r.address}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Tipe Gudang',
      dataIndex: 'type',
      key: 'type',
      width: 160,
      render: (t: string) => (
        <Tag color={TYPE_COLORS[t] || 'default'} style={{ borderRadius: 6, fontSize: 11 }}>
          {TYPE_LABELS[t] || t}
        </Tag>
      ),
    },
    {
      title: 'PIC (Penanggung Jawab)',
      key: 'pic',
      width: 190,
      render: (_: unknown, r: WarehouseRecord) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1E293B', fontSize: 12 }}>{r.picName || '-'}</div>
          {r.picPhone && <div style={{ fontSize: 11, color: '#64748B' }}>{r.picPhone}</div>}
        </div>
      ),
    },
    {
      title: 'Kapasitas',
      key: 'capacity',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, r: WarehouseRecord) =>
        r.capacity ? (
          <strong style={{ color: '#0369A1' }}>{r.capacity.toLocaleString('id-ID')} {r.capacityUnit}</strong>
        ) : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s: string) => (
        <Tag color={s === 'ACTIVE' ? 'success' : 'default'} style={{ borderRadius: 20, fontWeight: 600 }}>
          {s === 'ACTIVE' ? 'Aktif' : 'Nonaktif'}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 160,
      align: 'right' as const,
      render: (_: unknown, r: WarehouseRecord) => (
        <Space size="small">
          <Button
            size="small" type="default" icon={<EyeOutlined />}
            onClick={() => router.push(`/master/warehouses/${r.id}`)}
            style={{ borderRadius: 6, color: '#6366F1', borderColor: '#C7D2FE', fontSize: 12 }}
          >
            Detail
          </Button>
          {isManager && (
            <>
              <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(r)} style={{ borderRadius: 6 }} />
              <Popconfirm
                title="Hapus Gudang"
                description={`Hapus gudang '${r.name}'? Gudang dengan stok aktif tidak dapat dihapus.`}
                onConfirm={() => handleDelete(r.id)}
                okText="Ya, Hapus" cancelText="Batal" okButtonProps={{ danger: true }}
              >
                <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: 40 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Title level={3} style={{ margin: 0, color: '#0F172A', fontWeight: 800 }}>
              Modul Warehouse (Gudang)
            </Title>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#0369A1', background: '#E0F2FE', border: '1px solid #BAE6FD', padding: '3px 10px', borderRadius: 20 }}>
              Manajemen Lokasi Penyimpanan
            </span>
          </div>
          <Text type="secondary" style={{ fontSize: 13, marginTop: 4, display: 'block' }}>
            Kelola data gudang penyimpanan fisik dan referensi lokasi inventaris barang perusahaan.
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchWarehouses} style={{ borderRadius: 10 }}>Segarkan</Button>
          {isManager && (
            <Button
              type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}
              style={{ borderRadius: 10, background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)', fontWeight: 600 }}
            >
              Tambah Gudang Baru
            </Button>
          )}
        </Space>
      </div>

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { label: 'Total Gudang Terdaftar', value: `${warehouses.length} Gudang`, sub: 'Semua area penyimpanan', color: '#EEF2FF', border: '#E0E7FF', text: '#1E1B4B' },
          { label: 'Gudang Aktif Beroperasi', value: `${activeCount} Gudang`, sub: 'Siap terima stok', color: '#F0FDF4', border: '#BBF7D0', text: '#064E3B' },
          { label: 'Zona Karantina & Khusus', value: `${warehouses.filter((w) => w.type === 'QUARANTINE').length} Gudang`, sub: 'Isolasi barang defect/QC', color: '#FEF2F2', border: '#FECACA', text: '#991B1B' },
        ].map((kpi) => (
          <Col xs={24} sm={8} key={kpi.label}>
            <div style={{ background: `linear-gradient(135deg, #FFFFFF 0%, ${kpi.color} 100%)`, padding: '18px 20px', borderRadius: 14, border: `1px solid ${kpi.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>{kpi.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: kpi.text, marginTop: 4 }}>{kpi.value}</div>
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>{kpi.sub}</div>
            </div>
          </Col>
        ))}
      </Row>

      <Card style={{ borderRadius: 16, border: '1px solid #F1F5F9' }} styles={{ body: { padding: '18px 20px' } }}>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={14} md={12}>
            <Input
              placeholder="Cari berdasarkan Kode, Nama Gudang, atau Alamat..."
              prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} allowClear style={{ borderRadius: 10 }}
            />
          </Col>
          <Col xs={24} sm={10} md={8}>
            <Select
              value={typeFilter} onChange={setTypeFilter} style={{ width: '100%' }}
              options={[{ value: 'ALL', label: 'Semua Tipe Gudang' }, ...WAREHOUSE_TYPES]}
            />
          </Col>
        </Row>
        <Table rowKey="id" columns={columns} dataSource={warehouses} loading={loading} pagination={false} size="middle" />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={editingWh ? `Edit Gudang: ${editingWh.code}` : 'Tambah Gudang Baru'}
        open={modalOpen} onOk={handleSubmitForm} onCancel={() => setModalOpen(false)}
        confirmLoading={submitting} okText={editingWh ? 'Simpan Perubahan' : 'Tambahkan Gudang'} cancelText="Batal" width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={9}>
              <Form.Item name="code" label="Kode Gudang" rules={[{ required: true }]}>
                <Input placeholder="Contoh: GU-001" style={{ borderRadius: 8, textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col span={15}>
              <Form.Item name="name" label="Nama Gudang" rules={[{ required: true }]}>
                <Input placeholder="Nama gudang" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="type" label="Tipe Gudang" rules={[{ required: true }]}>
                <Select options={WAREHOUSE_TYPES} placeholder="Pilih tipe..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select options={[{ value: 'ACTIVE', label: 'Aktif' }, { value: 'INACTIVE', label: 'Nonaktif' }]} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label="Alamat / Lokasi Gudang">
            <Input placeholder="Area, blok, atau lokasi fisik gudang" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="picName" label="Nama Penanggung Jawab (PIC)">
                <Input style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="picPhone" label="Telepon PIC">
                <Input style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="capacity" label="Kapasitas">
                <InputNumber style={{ width: '100%', borderRadius: 8 }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="capacityUnit" label="Satuan Kapasitas">
                <Select options={[{ value: 'pallet', label: 'Pallet' }, { value: 'm²', label: 'm² (meter persegi)' }, { value: 'ton', label: 'Ton' }]} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Catatan Operasional">
            <Input.TextArea rows={2} style={{ borderRadius: 8 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
